import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { MapPin, Filter, CheckCircle, Star, Clock, Users, Lock, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PLACEHOLDER_IMG = "https://static.prod-images.emergentagent.com/jobs/1bc1e0bc-6716-4e3a-9c11-cd3b13fbaefa/images/1c30bf8bedb2266f7b8ce4ba5eca516cc891a4de6141fdb5b7fea0d0ccfddc2e.png";

const POSITIONS = ["", "Goalkeeper", "Centre-Back", "Left-Back", "Right-Back", "Defensive Midfielder", "Central Midfielder", "Attacking Midfielder", "Left Winger", "Right Winger", "Striker", "Centre-Forward"];

function PlayerCard({ p }) {
  const identity = p.onboarding_data?.identity || {};
  const gen = p.generated_profile || {};
  const imgUrl = p.profile_image_path ? `${BACKEND_URL}/api/media/${p.profile_image_path}` : PLACEHOLDER_IMG;

  return (
    <Link to={`/player/${p.slug}`} className="card-dark group overflow-hidden" data-testid={`player-card-${p.slug}`}>
      <div className="aspect-[3/4] overflow-hidden relative">
        <img src={imgUrl} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="flex flex-wrap gap-1.5 mb-2">
            {p.is_featured && <span className="badge-gold text-[8px]">FEATURED</span>}
            {p.is_reviewed && <span className="text-[8px] font-bold uppercase tracking-wider text-[#D4AF37] border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-1.5 py-0.5 flex items-center gap-0.5"><CheckCircle size={7} />VERIFIED</span>}
          </div>
          <h3 className="text-lg font-bold text-[#F5F5F5] uppercase tracking-tight">{p.name}</h3>
          <div className="flex items-center gap-2 text-xs text-[#A3A3A3] mt-1">
            {identity.primary_position && <span className="text-[#D4AF37]">{identity.primary_position}</span>}
            {identity.nationality && <span className="flex items-center gap-1"><MapPin size={10} />{identity.nationality}</span>}
          </div>
          {gen.headline && <p className="text-xs text-[#A3A3A3] mt-2 line-clamp-2">{gen.headline}</p>}
        </div>
      </div>
    </Link>
  );
}

export default function Directory() {
  const { user, loading: authLoading } = useAuth();
  const isLoggedIn = user && user !== false;
  const hasPaidAccess = isLoggedIn && (user.plan || user.role === "scout" || user.role === "admin");
  const [profiles, setProfiles] = useState([]);
  const [featuredProfiles, setFeaturedProfiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [position, setPosition] = useState("");
  const [country, setCountry] = useState("");
  const [featured, setFeatured] = useState(false);
  const [verified, setVerified] = useState(false);
  const [showFeaturedSection, setShowFeaturedSection] = useState(true);

  // Load featured profiles once
  useEffect(() => {
    const loadFeatured = async () => {
      try {
        const { data } = await api.get("/directory?featured=true&limit=4");
        setFeaturedProfiles(data.profiles || []);
      } catch {}
    };
    loadFeatured();
  }, []);

  // Load main directory
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page: String(page), limit: "12" });
        if (position) params.append("position", position);
        if (country) params.append("country", country);
        if (featured) params.append("featured", "true");
        const { data } = await api.get(`/directory?${params}`);
        let results = data.profiles || [];
        if (verified) {
          results = results.filter(p => p.is_reviewed);
        }
        setProfiles(results);
        setTotal(verified ? results.length : (data.total || 0));
      } catch {}
      setLoading(false);
    };
    load();
  }, [page, position, country, featured, verified]);

  // Sort profiles: recently updated first
  const sortedProfiles = [...profiles].sort((a, b) => {
    const dateA = new Date(a.updated_at || a.published_at || a.created_at || 0);
    const dateB = new Date(b.updated_at || b.published_at || b.created_at || 0);
    return dateB - dateA;
  });

  const hasFilters = position || country || featured || verified;

  return (
    <div className="bg-[#0A0A0A] min-h-screen py-16" data-testid="directory-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="mb-12">
          <div className="overline mb-2">PLAYER NETWORK</div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter uppercase text-[#F5F5F5] mb-4">
            PLAYER DIRECTORY
          </h1>
          <p className="text-[#A3A3A3] max-w-2xl">
            Browse structured player profiles across the network. Every profile follows a consistent, scout-ready format designed for clarity and quick assessment.
          </p>
        </div>

        {/* Featured Players — only for members */}
        {hasPaidAccess && showFeaturedSection && featuredProfiles.length > 0 && !hasFilters && (
          <section className="mb-14" data-testid="featured-section">
            <div className="flex items-center gap-3 mb-6">
              <Star size={16} className="text-[#D4AF37]" />
              <h2 className="text-sm font-bold tracking-wider uppercase text-[#F5F5F5]">FEATURED PLAYERS</h2>
              <span className="text-xs text-[#A3A3A3]">Selected for visibility based on presentation quality</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {featuredProfiles.map((p) => <PlayerCard key={p.slug} p={p} />)}
            </div>
          </section>
        )}

        {/* Gate for non-members — fully locked, no preview */}
        {!authLoading && !hasPaidAccess && (
          <div data-testid="directory-gate">
            <div className="py-20 flex justify-center">
              <div className="bg-[#0E0E0E] border border-[#1A1A1A] p-10 text-center max-w-md">
                <Lock size={28} className="text-[#D4AF37] mx-auto mb-4" />
                <h3 className="text-xl font-bold tracking-tight uppercase text-[#F5F5F5] mb-3">PRIVATE PLAYER ENVIRONMENT</h3>
                <p className="text-sm text-[#A3A3A3] mb-6 leading-relaxed">
                  This is a private player environment. Access is restricted to members and approved scouts.
                </p>
                <div className="flex flex-col gap-3">
                  <Link to="/start" className="btn-gold flex items-center justify-center gap-2 text-sm" data-testid="gate-profile">
                    GET YOUR PROFILE <ArrowRight size={16} />
                  </Link>
                  <Link to="/scout-apply" className="btn-outline flex items-center justify-center gap-2 text-sm" data-testid="gate-scout">
                    APPLY AS SCOUT
                  </Link>
                  {!isLoggedIn && (
                    <Link to="/login" className="text-xs text-[#555] hover:text-[#A3A3A3] transition-colors mt-1" data-testid="gate-login">
                      Already a member? Sign in
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Full directory for members */}
        {hasPaidAccess && (
          <>
        {/* Filters */}
        <div className="card-dark p-4 mb-6 flex flex-col sm:flex-row gap-3 items-stretch" data-testid="directory-filters">
          <div className="flex-1">
            <select
              value={position}
              onChange={(e) => { setPosition(e.target.value); setPage(1); }}
              className="input-dark w-full"
              data-testid="filter-position"
            >
              <option value="">All Positions</option>
              {POSITIONS.filter(Boolean).map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <input
              type="text"
              value={country}
              onChange={(e) => { setCountry(e.target.value); setPage(1); }}
              className="input-dark w-full"
              placeholder="Filter by country..."
              data-testid="filter-country"
            />
          </div>
          <button
            onClick={() => { setFeatured(!featured); setPage(1); }}
            className={`px-4 py-3 font-bold text-xs uppercase tracking-wider rounded-sm transition-all flex items-center gap-1.5 ${
              featured ? "bg-[#D4AF37] text-black" : "bg-[#1A1A1A] text-[#A3A3A3] border border-[#262626] hover:border-[#D4AF37]"
            }`}
            data-testid="filter-featured"
          >
            <Star size={12} /> FEATURED
          </button>
          <button
            onClick={() => { setVerified(!verified); setPage(1); }}
            className={`px-4 py-3 font-bold text-xs uppercase tracking-wider rounded-sm transition-all flex items-center gap-1.5 ${
              verified ? "bg-[#D4AF37] text-black" : "bg-[#1A1A1A] text-[#A3A3A3] border border-[#262626] hover:border-[#D4AF37]"
            }`}
            data-testid="filter-verified"
          >
            <CheckCircle size={12} /> VERIFIED
          </button>
        </div>

        {/* Results header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Users size={14} className="text-[#555]" />
            <span className="text-sm text-[#A3A3A3]">{total} player{total !== 1 ? "s" : ""} in the network</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-[#555]">
            <Clock size={12} />
            <span>Recently updated first</span>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : sortedProfiles.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#A3A3A3] text-lg mb-2">No players found matching your criteria.</p>
            <p className="text-[#555] text-sm">Try adjusting your filters or browse the full directory.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {sortedProfiles.map((p) => <PlayerCard key={p.slug} p={p} />)}
          </div>
        )}

        {/* Pagination */}
        {total > 12 && (
          <div className="flex justify-center gap-2 mt-10">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="btn-outline text-sm px-4 py-2 disabled:opacity-30" data-testid="page-prev">PREV</button>
            <span className="text-[#A3A3A3] text-sm flex items-center px-4">Page {page}</span>
            <button onClick={() => setPage(page + 1)} disabled={sortedProfiles.length < 12} className="btn-outline text-sm px-4 py-2 disabled:opacity-30" data-testid="page-next">NEXT</button>
          </div>
        )}

        {/* Network info */}
        <div className="mt-16 border-t border-[#262626] pt-10 text-center" data-testid="network-info">
          <h3 className="text-sm font-bold tracking-wider uppercase text-[#F5F5F5] mb-3">ABOUT THE NETWORK</h3>
          <p className="text-sm text-[#A3A3A3] max-w-2xl mx-auto leading-relaxed mb-4">
            Every profile on the Elite Scouting Network follows a consistent, structured format designed for clarity. Profiles marked as <span className="text-[#D4AF37]">Verified</span> have been reviewed for completeness and presentation quality. <span className="text-[#D4AF37]">Featured</span> players are selected for visibility based on the standard of their profile.
          </p>
        </div>
          </>
        )}
      </div>
    </div>
  );
}
